import { createApp, defineAsyncComponent } from "vue";
// Styling
import "@/index.css";
import 'primeicons/primeicons.css'

// TMI-specific components, plugins & services
import App from "@/App.vue";

import ContentFiltersPlugin from "@/plugins/ContentFiltersPlugin";
import PrimeVuePlugin from "@/plugins/PrimeVuePlugin";
import SectorToolsPlugin from "@/plugins/SectorToolsPlugin";

import router, { myArticlesKey, myWebinarsKey, SectorsAsync, } from "@/router";

import PiniaPlugin from "@/plugins/PiniaPlugin";

import * as tmi from "@/constants";

import {
    articleKey,
    bonusReportKey,
    contentWrapperKey,
    courseKey,
    etfListingKey,
    fiveStarModelPortfolioKey,
    lessonsKey,
    majorIndexBreadthKey,
    modelPortfolioKey,
    notFoundKey,
    nyseBpiKey,
    portfolioBuilderKey,
    positionSizeCalculatorKey,
    relativeStrengthMatrixKey,
    scannerKey,
    sectorAlertsKey,
    sectorsKey,
    tradeAlertKey,
    tradeAlertSearchKey,
    tradeOfTheDayKey,
    vimeoKey,
    webinarKey,
} from "@/types/products/product-definitions";
import LocalForagePlugin from "@/plugins/LocalForagePlugin";
import ApexChartsPlugin from "@/plugins/ApexChartsPlugin";
import LabelServicePlugin from "@/plugins/LabelServicePlugin";
import AdServicePlugin from "@/plugins/AdServicePlugin";
import ProductContextsPlugin from "@/plugins/ProductContextsPlugin";

import { createHead } from '@unhead/vue/client'

// Make touch/wheel event listeners passive by default to improve scroll performance
// This fixes warnings from third-party libraries (PrimeVue) that don't mark their listeners as passive
( function makeEventListenersPassive() {
    const originalAddEventListener = EventTarget.prototype.addEventListener;
    const passiveEvents = new Set( [ 'touchstart', 'touchmove', 'wheel', 'mousewheel' ] );

    EventTarget.prototype.addEventListener = function (
        type: string,
        listener: EventListenerOrEventListenerObject | null,
        options?: boolean | AddEventListenerOptions
    ) {
        if ( passiveEvents.has( type ) ) {
            const newOptions: AddEventListenerOptions =
                typeof options === 'boolean'
                    ? { capture: options, passive: true }
                    : { ...options, passive: options?.passive ?? true };
            return originalAddEventListener.call( this, type, listener, newOptions );
        }
        return originalAddEventListener.call( this, type, listener, options );
    };
} )();

const unheadClient = createHead();

const app = createApp( App );
app.config.globalProperties.$toasterTimeOut = 5000;

app.use( LocalForagePlugin )
    .use( PiniaPlugin )
    .use( SectorToolsPlugin )
    .use( router )
    .use( ContentFiltersPlugin )
    .use( ProductContextsPlugin )
    .use( PrimeVuePlugin )
    .use( ApexChartsPlugin )
    .use( LabelServicePlugin )
    .use( AdServicePlugin )
    .use( unheadClient );

router.isReady().then( () => {
    // Product subcomponents
    app.component( articleKey, defineAsyncComponent( () => import( '@/components/wrappers/ProductArticleSearchWrapper.vue' ) ) );
    app.component( bonusReportKey, defineAsyncComponent( () => import( '@/components/wrappers/ProductBonusReportSearchWrapper.vue' ) ) );
    app.component( contentWrapperKey, defineAsyncComponent( () => import( '@/components/wrappers/ProductContentWrapper.vue' ) ) );
    app.component( lessonsKey, defineAsyncComponent( () => import( '@/components/wrappers/ProductCourseWrapper.vue' ) ) );
    app.component( modelPortfolioKey, defineAsyncComponent( () => import( '@/components/portfolios/ModelPortfoliosTables.vue' ) ) );
    app.component( fiveStarModelPortfolioKey, defineAsyncComponent( () => import( '@/components/portfolios/FiveStarModelPortfolioContainer.vue' ) ) );
    app.component( scannerKey, defineAsyncComponent( () => import( '@/components/LaunchScanner.vue' ) ) );
    app.component( tradeAlertKey, defineAsyncComponent( () => import( '@/components/wrappers/ProductTradeAlertSearchWrapper.vue' ) ) );
    app.component( webinarKey, defineAsyncComponent( () => import( '@/components/wrappers/ProductWebinarSearchWrapper.vue' ) ) );
    app.component( vimeoKey, defineAsyncComponent( () => import( '@/components/wrappers/ProductVimeoWrapper.vue' ) ) );

    // Common Tool Subcomponents
    app.component( etfListingKey, defineAsyncComponent( () => import( '@/components/EtfList.vue' ) ) );
    app.component( majorIndexBreadthKey, defineAsyncComponent( () => import( '@/pages/Tools/MajorIndexBreadth.vue' ) ) );
    app.component( notFoundKey, defineAsyncComponent( () => import( '@/components/errors/NotFoundError.vue') ) );
    app.component( nyseBpiKey, defineAsyncComponent( () => import( '@/pages/BPI.vue' ) ) );
    app.component( portfolioBuilderKey, defineAsyncComponent( () => import( '@/pages/Tools/PositionKeyTool.vue' ) ) );
    app.component( positionSizeCalculatorKey, defineAsyncComponent( () => import( '@/pages/Tools/PositionSizeCalculator.vue' ) ) );
    app.component( relativeStrengthMatrixKey, defineAsyncComponent( () => import( '@/pages/Tools/RsMatrix.vue' ) ) );
    app.component( sectorsKey, defineAsyncComponent( SectorsAsync ) );
    // app.component( stockReplacementStrategyCalculatorKey, defineAsyncComponent( SrsCalculatorAsync ) );
    app.component( tmi.SpreadCalculatorKey, defineAsyncComponent( () => import( '@/pages/Tools/SpreadCalculator.vue' ) ) );

    // Alerts Subcomponents
    app.component( sectorAlertsKey, defineAsyncComponent( () => import( '@/components/sectors/SectorAlertsList.vue' ) ) );
    app.component( tradeAlertSearchKey, defineAsyncComponent( () => import( '@/components/search/TradeAlertSearch.vue' ) ) );
    app.component( tradeOfTheDayKey, defineAsyncComponent( () => import( '@/pages/TradeOfTheDay.vue' ) ) );

    app.component( myArticlesKey, defineAsyncComponent( () => import( '@/components/layout/MyLibrary.vue' ) ) );
    app.component( myWebinarsKey, defineAsyncComponent( () => import( '@/components/layout/MyLibrary.vue' ) ) );

    // Subcomponents of priorly listed components
    app.component( courseKey, defineAsyncComponent( () => import( '@/components/courses/CourseOverview.vue' ) ) );

    app.mount( "#app" );
} );
